import { EMRdetailsProps, IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import DiagnosisSelector from "./DiagnosisSelector";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDiagnosisListAPI } from "@/app/hooks/api_hooks/emr/useDiagnosisDataApi";
import { useForm } from "react-hook-form";

export interface MedicalInstructionsFromProps {
  subjective_history: string | null;
  physical_examinations: string | null;
  medical_instructions: string | null;
  diagnosis: string | null;
  tentative_diagnosis: string[];
  definitive_diagnosis: string[];
}

type SectionType = "subjective_history" | "physical_examinations" | "medical_instructions" | "diagnosis";

const MedicalInstructionsComponent = ({ emrDetails }: EMRdetailsProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");
  const { execute: fetchDiagnosisList, diagnosisList } = useDiagnosisListAPI();

  const formHook = useForm<MedicalInstructionsFromProps>({
    mode: "onChange",
    defaultValues: {
      tentative_diagnosis: [],
      definitive_diagnosis: [],
    },
  });

  const debouncedUpdate = useMemo(
    () =>
      debounce((section: SectionType, value: string) => {
        const constructedBody: IEMRUpdatePayloadProps = {
          [section]: value,
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        updateEmrAPI(emr_id, constructedBody);
      }, 500),
    [emr_id, emr_status, emrDetails?.emr_status, updateEmrAPI]
  );

  const isFirstLoad = useRef(true);

  const handleInputChange = useCallback(
    (section: SectionType) => {
      const value = formHook.getValues(section);
      debouncedUpdate(section, value);
    },
    [formHook, debouncedUpdate]
  );

  // Single unified handler for both diagnosis types
  const handleDiagnosisChange = useCallback(
    (diagnosisType: "tentative_diagnosis" | "definitive_diagnosis", selectedItems: ISelectMenuItemData[]) => {
      const selectedIds = selectedItems.map((item) => item.id);
      
      formHook.setValue(diagnosisType, selectedIds, {
        shouldValidate: true,
        shouldDirty: true,
      });
      
      console.log(`${diagnosisType === "tentative_diagnosis" ? "Tentative" : "Definitive"} Diagnosis Selected:`, selectedIds);
    },
    [formHook]
  );

  // Single unified blur handler for both diagnosis types
  const handleDiagnosisBlur = useCallback(() => {
    const tentativeIds = formHook.getValues("tentative_diagnosis");
    const definitiveIds = formHook.getValues("definitive_diagnosis");
    
    const constructedBody: IEMRUpdatePayloadProps = {
      tentative_diagnosis: tentativeIds,
      definitive_diagnosis: definitiveIds,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };
    
    updateEmrAPI(emr_id, constructedBody);
    console.log("Diagnosis Updated via Blur:", { tentativeIds, definitiveIds });
  }, [formHook, emrDetails?.emr_status, emr_status, emr_id, updateEmrAPI]);

  useEffect(() => {
    if (emrDetails) {
      isFirstLoad.current = false; 
      formHook.reset({
        subjective_history: emrDetails?.subjective_history || "",
        physical_examinations: emrDetails?.physical_examinations || "",
        medical_instructions: emrDetails?.medical_instructions || "",
        diagnosis: emrDetails?.diagnosis || "",
        tentative_diagnosis: emrDetails?.tentative_diagnosis || [],
        definitive_diagnosis: emrDetails?.definitive_diagnosis || [],
      });
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails, formHook]);

  useEffect(() => {
    fetchDiagnosisList();
  }, []);

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

 const diagnosisOptions: ISelectMenuItemData[] = useMemo(() => {
  return diagnosisList.map((item) => ({
    id: item.id,
    title: item.title,
    aliases: item.aliases,
  }));
}, [diagnosisList]);

  const defaultTentativeSelected: ISelectMenuItemData[] = useMemo(() => {
    const tentativeDiagnosis = emrDetails?.tentative_diagnosis || [];
    
    return tentativeDiagnosis.map((diagnosisId) => {
      // First check if it exists in the diagnosis options
      const existingOption = diagnosisOptions.find((option) => option.id === diagnosisId);
      
      if (existingOption) {
        return existingOption;
      }
      
      // If not found in options, it's a custom diagnosis - create it
      return {
        id: diagnosisId,
        title: diagnosisId,
        state: {},
      };
    });
  }, [diagnosisOptions, emrDetails?.tentative_diagnosis]);

  const defaultDefinitiveSelected: ISelectMenuItemData[] = useMemo(() => {
    const definitiveDiagnosis = emrDetails?.definitive_diagnosis || [];
    
    return definitiveDiagnosis.map((diagnosisId) => {
      // First check if it exists in the diagnosis options
      const existingOption = diagnosisOptions.find((option) => option.id === diagnosisId);
      
      if (existingOption) {
        return existingOption;
      }
      
      // If not found in options, it's a custom diagnosis - create it
      return {
        id: diagnosisId,
        title: diagnosisId,
        state: {},
      };
    });
  }, [diagnosisOptions, emrDetails?.definitive_diagnosis]);

  return (
    <div className="w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Add Instructions</h3>
      <div className="w-full flex flex-col gap-3 mt-2">
        {/* Subjective History Section */}
        <div className="bg-[#F9FAFB] rounded-xl p-2">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Subjective History</h3>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <textarea
              className="w-full border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Write if anything requires..."
              {...formHook.register("subjective_history", {
                required: false,
                onChange: () => handleInputChange("subjective_history"),
              })}
            ></textarea>
          </div>
        </div>

        {/* Physical Examination Section */}
        <div className="bg-[#F9FAFB] rounded-xl p-2">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Physical Examination</h3>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <textarea
              className="w-full border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Write if anything requires..."
              {...formHook.register("physical_examinations", {
                required: false,
                onChange: () => handleInputChange("physical_examinations"),
              })}
            ></textarea>
          </div>
        </div>

        {/* Medical Instructions Section — home-care advice for the pet parent */}
        <div className="bg-[#F9FAFB] rounded-xl p-2">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Medical Instructions</h3>
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <textarea
              className="w-full border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Home-care instructions for the pet parent..."
              rows={4}
              {...formHook.register("medical_instructions", {
                required: false,
                onChange: () => handleInputChange("medical_instructions"),
              })}
            ></textarea>
          </div>
        </div>

        {/* Diagnosis Section with DiagnosisSelector */}
        <div className="bg-[#F9FAFB] rounded-xl p-2">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42">Diagnosis</h3>
          
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <DiagnosisSelector
              key={`tentative-${defaultTentativeSelected.map(d => d.id).join('-')}`}
              title="Tentative Diagnosis"
              data={diagnosisOptions}
              placeholder="Add Or Search Tentative Diagnosis"
              onSelectionChange={(items) => handleDiagnosisChange("tentative_diagnosis", items)}
              onBlur={handleDiagnosisBlur}
              register={formHook.register("tentative_diagnosis")}
              fieldError={formHook.formState.errors.tentative_diagnosis}
              errorMessages={[]}
              defaultSelected={defaultTentativeSelected}
            />
          </div>

          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <DiagnosisSelector
              key={`definitive-${defaultDefinitiveSelected.map(d => d.id).join('-')}`}
              title="Definitive Diagnosis"
              data={diagnosisOptions}
              placeholder="Add Or Search Definitive Diagnosis"
              onSelectionChange={(items) => handleDiagnosisChange("definitive_diagnosis", items)}
              onBlur={handleDiagnosisBlur}
              register={formHook.register("definitive_diagnosis")}
              fieldError={formHook.formState.errors.definitive_diagnosis}
              errorMessages={[]}
              defaultSelected={defaultDefinitiveSelected}
            />
          </div>

          {/* Original Diagnosis Textarea */}
          <div className="bg-[#FFFFFF] rounded-xl p-[10px] mt-2">
            <h4 className="text-[#6C727F] text-xs font-medium mb-2">Additional Notes</h4>
            <textarea
              className="w-full border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
              placeholder="Write if anything requires..."
              rows={4}
              {...formHook.register("diagnosis", {
                required: false,
                onChange: () => handleInputChange("diagnosis"),
              })}
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalInstructionsComponent;
